import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Calendar,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  SYMPTOM_OPTIONS,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from "@nfp-tracker/components";
import {
  BLEEDING_OPTIONS,
  buildLegacyMucusSummary,
  cn,
  MUCUS_COLOR_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS,
  MUCUS_FREQUENCY_OPTIONS,
  normalizeOvulationTest,
  normalizeTemperatureInput,
  OVULATION_TEST_OPTIONS,
  usesExpandedMucus,
} from "@nfp-tracker/utils";
import type { OvulationTestResult } from "@nfp-tracker/utils";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, format, parseISO, subDays } from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import { captureException, trackEvent } from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";
import {
  mergeObservationRow,
  observationsQueryKey,
  useObservationsCache,
  type ObservationRow,
} from "../hooks/useObservationsCache";
import { useOwnerContext } from "../hooks/useOwnerContext";

type MonitorValue = "LOW" | "HIGH" | "PEAK" | null;

type DayDraft = {
  isCycleStart: boolean;
  bleeding: number | null;
  mucusConsistency: string | null;
  mucusColor: string | null;
  mucusFrequency: string | null;
  monitor: MonitorValue;
  ovulationTest: OvulationTestResult | null;
  temperature: string;
  intercourse: boolean;
  symptoms: string[];
  notes: string;
};

const EMPTY_DRAFT: DayDraft = {
  isCycleStart: false,
  bleeding: null,
  mucusConsistency: null,
  mucusColor: null,
  mucusFrequency: null,
  monitor: null,
  ovulationTest: null,
  temperature: "",
  intercourse: false,
  symptoms: [],
  notes: "",
};

const rowToDraft = (row: ObservationRow): DayDraft => ({
  isCycleStart: row.is_cycle_start,
  bleeding: row.bleeding,
  mucusConsistency: row.mucus_consistency,
  mucusColor: row.mucus_color,
  mucusFrequency: row.mucus_frequency,
  monitor: row.monitor as MonitorValue,
  ovulationTest: normalizeOvulationTest(row.ovulation_test),
  temperature: row.temperature_f != null ? String(row.temperature_f) : "",
  intercourse: Boolean(row.intercourse),
  symptoms: row.symptoms ?? [],
  notes: row.notes ?? "",
});

const MONITOR_OPTIONS: { value: NonNullable<MonitorValue>; label: string }[] = [
  { value: "LOW", label: "Low" },
  { value: "HIGH", label: "High" },
  { value: "PEAK", label: "Peak" },
];

const DAY_COUNT = 14;
const NONE_VALUE = "__none__";
const dateKeyOf = (date: Date) => format(date, "yyyy-MM-dd");

const parseDateParam = (value: string | null): Date | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Window is tracked by its end date; this derives that end date from a desired start date,
 * clamping so the window never reaches into the future. */
const windowEndForStart = (start: Date, today: Date): Date => {
  const desiredEnd = addDays(start, DAY_COUNT - 1);
  return desiredEnd > today ? today : desiredEnd;
};

const FieldRow = ({
  label,
  days,
  drafts,
  renderCell,
}: {
  label: string;
  days: Date[];
  drafts: Record<string, DayDraft>;
  renderCell: (dateKey: string, draft: DayDraft) => React.ReactNode;
}) => (
  <TableRow>
    <TableCell className="sticky left-0 z-10 whitespace-nowrap bg-background font-medium">
      {label}
    </TableCell>
    {days.map((day) => {
      const dateKey = dateKeyOf(day);
      return (
        <TableCell key={dateKey} className="text-center">
          {renderCell(dateKey, drafts[dateKey] ?? EMPTY_DRAFT)}
        </TableCell>
      );
    })}
  </TableRow>
);

/** Edge fade thickness, in px — matched to the gradient overlay width/height so the fade
 * disappears right as the edge is actually reached. */
const SCROLL_EDGE_THRESHOLD = 4;

/**
 * Fields as rows, days as columns, opened full-screen (like the calendar's fullscreen
 * dialog) so a whole week/two can be scanned and edited without day-by-day navigation.
 * Loads existing rows from the shared observations cache and batch-upserts edited days on
 * save — same field mapping Observations.tsx's single-day save uses.
 */
const BulkEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const today = useMemo(() => new Date(), []);
  const [windowEnd, setWindowEnd] = useState(() => {
    const requestedStart = parseDateParam(searchParams.get("date"));
    return windowEndForStart(requestedStart ?? today, today);
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, DayDraft>>({});
  const [dirtyDates, setDirtyDates] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollEdges, setScrollEdges] = useState({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });

  const ownerQuery = useOwnerContext();
  const ownerData = ownerQuery.data;
  const isOwnerReady = ownerData?.status === "ready";
  const couple = isOwnerReady ? ownerData.couple : null;
  const canWriteObservations = isOwnerReady ? ownerData.canWriteObservations : false;
  const cache = useObservationsCache(couple?.id, couple?.methods);
  const isLoading = ownerQuery.isLoading || (isOwnerReady && cache.isLoading);

  const days = useMemo(
    () => Array.from({ length: DAY_COUNT }, (_, i) => subDays(windowEnd, DAY_COUNT - 1 - i)),
    [windowEnd]
  );

  const todayKey = dateKeyOf(today);
  // Until some day is marked as a cycle start, cycle days can't be counted (see
  // derivedCycles.ts), so nudge towards marking one.
  const hasRecordedCycleStart =
    cache.cycles.some((c) => !c.start_inferred) ||
    Object.values(drafts).some((d) => d.isCycleStart);
  const canGoForward = dateKeyOf(windowEnd) < todayKey;

  // Hydrate drafts for any day in view that has a saved row and hasn't been locally edited
  // yet — dirty days are left alone so this can't clobber an in-progress edit when the cache
  // updates (e.g. right after this page's own save merges rows back in).
  useEffect(() => {
    if (!cache.rawObservations.length) return;
    setDrafts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const day of days) {
        const dateKey = dateKeyOf(day);
        if (dirtyDates.has(dateKey)) continue;
        const row = cache.rawObservations.find((r) => r.observation_date === dateKey);
        if (row) {
          next[dateKey] = rowToDraft(row);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache.rawObservations, days]);

  const updateScrollEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollEdges({
      left: el.scrollLeft > SCROLL_EDGE_THRESHOLD,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - SCROLL_EDGE_THRESHOLD,
      top: el.scrollTop > SCROLL_EDGE_THRESHOLD,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - SCROLL_EDGE_THRESHOLD,
    });
  }, []);

  useEffect(() => {
    updateScrollEdges();
    window.addEventListener("resize", updateScrollEdges);
    return () => window.removeEventListener("resize", updateScrollEdges);
  }, [updateScrollEdges, days]);

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const closeAndLeave = () => {
    setOpen(false);
    navigate(ROUTES.observations);
  };

  const handleClose = (next: boolean) => {
    if (next) {
      setOpen(true);
      return;
    }
    if (dirtyDates.size > 0) {
      setShowDiscardConfirm(true);
      return;
    }
    closeAndLeave();
  };

  const updateDraft = (dateKey: string, patch: Partial<DayDraft>) => {
    setDrafts((prev) => ({ ...prev, [dateKey]: { ...(prev[dateKey] ?? EMPTY_DRAFT), ...patch } }));
    setDirtyDates((prev) => (prev.has(dateKey) ? prev : new Set(prev).add(dateKey)));
  };

  const toggleSymptom = (dateKey: string, symptom: string) => {
    const draft = drafts[dateKey] ?? EMPTY_DRAFT;
    const next = draft.symptoms.includes(symptom)
      ? draft.symptoms.filter((s) => s !== symptom)
      : [...draft.symptoms, symptom];
    updateDraft(dateKey, { symptoms: next });
  };

  const handleJumpToDate = (day: Date) => {
    setWindowEnd(windowEndForStart(day, today));
    setIsDatePickerOpen(false);
  };

  const handleSaveAll = async () => {
    if (!couple || !canWriteObservations || saving) return;
    const dirtyList = Array.from(dirtyDates);
    if (!dirtyList.length) {
      toast({ title: "Nothing to save", description: "No changes yet." });
      return;
    }

    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const isExpandedMucusMethod = usesExpandedMucus(couple.methods);

      const rows = dirtyList.map((dateKey) => {
        const draft = drafts[dateKey] ?? EMPTY_DRAFT;
        const mucusColorToSave = isExpandedMucusMethod ? draft.mucusColor ?? "" : "";
        const normalizedTemperature = normalizeTemperatureInput(draft.temperature);
        return {
          owner_user_id: couple.id,
          observation_date: dateKey,
          is_cycle_start: draft.isCycleStart,
          bleeding: draft.bleeding,
          monitor: draft.monitor,
          ovulation_test: draft.ovulationTest || null,
          intercourse: draft.intercourse,
          mucus: buildLegacyMucusSummary(draft.mucusConsistency ?? "", mucusColorToSave),
          mucus_consistency: draft.mucusConsistency,
          mucus_color: mucusColorToSave || null,
          mucus_frequency: draft.mucusFrequency || null,
          notes: draft.notes || null,
          symptoms: draft.symptoms.length > 0 ? draft.symptoms : null,
          temperature_f: normalizedTemperature ? Number.parseFloat(normalizedTemperature) : null,
          created_by_user_id: user?.id,
        };
      });

      const { data: savedRows, error } = await supabase
        .from("observations")
        .upsert(rows, { onConflict: "owner_user_id,observation_date" })
        .select();

      if (error) throw error;

      queryClient.setQueryData<ObservationRow[]>(observationsQueryKey(couple.id), (prev) =>
        (savedRows ?? []).reduce((acc, row) => mergeObservationRow(acc, row), prev)
      );

      setDirtyDates(new Set());
      trackEvent("bulk_entry_save_succeeded", { dayCount: dirtyList.length });
      toast({
        title: "Saved",
        description: `${savedRows?.length ?? dirtyList.length} day${
          (savedRows?.length ?? dirtyList.length) === 1 ? "" : "s"
        } saved.`,
      });
    } catch (error: unknown) {
      trackEvent("bulk_entry_save_failed", { dayCount: dirtyList.length });
      captureException(error, { source: "bulk_entry_save" });
      toast({
        title: "Error",
        description: (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideCloseButton
        className="flex max-h-[92vh] w-[96vw] max-w-none flex-col overflow-hidden p-6 sm:rounded-lg"
      >
        <DialogTitle className="sr-only">Bulk Entry</DialogTitle>
        <DialogDescription className="sr-only">
          Full-screen view for backfilling multiple days of observations at once.
        </DialogDescription>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold">Bulk Entry</h1>
            <div className="flex items-center gap-3">
              {dirtyDates.size > 0 && (
                <span className="text-sm text-muted-foreground">
                  {dirtyDates.size} unsaved day{dirtyDates.size === 1 ? "" : "s"}
                </span>
              )}
              <Button
                size="sm"
                disabled={!canWriteObservations || saving || dirtyDates.size === 0}
                onClick={handleSaveAll}
              >
                {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Save changes
              </Button>
              <DialogClose asChild>
                <Button variant="ghost" size="icon" aria-label="Close bulk entry">
                  <X className="h-4 w-4" />
                </Button>
              </DialogClose>
            </div>
          </div>

          {!isLoading && !canWriteObservations && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
              Your access is read-only. Bulk entry is disabled.
            </div>
          )}

          {!isLoading && couple && canWriteObservations && !hasRecordedCycleStart && (
            <p className="text-xs text-muted-foreground">
              No cycle start recorded yet. Turn on Cycle start for the first day of a period so
              cycle days can be counted.
            </p>
          )}

          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWindowEnd((d) => subDays(d, DAY_COUNT))}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous {DAY_COUNT} days
            </Button>
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-sm text-muted-foreground">
                  <CalendarDays className="h-4 w-4" />
                  {format(days[0], "MMM d")} – {format(days[days.length - 1], "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={days[0]}
                  defaultMonth={days[0]}
                  disabled={{ after: today }}
                  onSelect={(day) => day && handleJumpToDate(day)}
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              disabled={!canGoForward}
              onClick={() => setWindowEnd((d) => subDays(d, -DAY_COUNT))}
            >
              Next {DAY_COUNT} days
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          <Card className="relative min-h-0 flex-1 overflow-hidden p-0">
            {isLoading ? (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading your observations…
              </div>
            ) : !couple ? (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                Couldn't load your account. Close this and reopen from Observations.
              </div>
            ) : (
              <>
                <div
                  ref={scrollRef}
                  onScroll={updateScrollEdges}
                  className="h-full w-full overflow-auto"
                >
                  <table className="w-full caption-bottom text-sm">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 top-0 z-30 min-w-[120px] bg-background">
                          Field
                        </TableHead>
                        {days.map((day) => {
                          const dateKey = dateKeyOf(day);
                          const isToday = dateKey === todayKey;
                          return (
                            <TableHead
                              key={dateKey}
                              className={cn(
                                "sticky top-0 z-20 min-w-[130px] bg-background text-center",
                                isToday && "bg-primary/5"
                              )}
                            >
                              <div className="font-semibold">{format(day, "EEE")}</div>
                              <div className="text-xs text-muted-foreground">
                                {format(day, "MMM d")}
                              </div>
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <FieldRow
                        label="Cycle start"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <Switch
                            checked={draft.isCycleStart}
                            disabled={!canWriteObservations}
                            onCheckedChange={(checked) =>
                              updateDraft(dateKey, { isCycleStart: checked })
                            }
                          />
                        )}
                      />

                      <FieldRow
                        label="Bleeding"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <Select
                            disabled={!canWriteObservations}
                            value={draft.bleeding == null ? NONE_VALUE : String(draft.bleeding)}
                            onValueChange={(value) =>
                              updateDraft(dateKey, {
                                bleeding: value === NONE_VALUE ? null : Number(value),
                              })
                            }
                          >
                            <SelectTrigger className="h-9 text-xs">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE_VALUE}>—</SelectItem>
                              {BLEEDING_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={String(opt.value)}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />

                      <FieldRow
                        label="Mucus"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <Select
                            disabled={!canWriteObservations}
                            value={draft.mucusConsistency ?? NONE_VALUE}
                            onValueChange={(value) =>
                              updateDraft(dateKey, {
                                mucusConsistency: value === NONE_VALUE ? null : value,
                              })
                            }
                          >
                            <SelectTrigger className="h-9 text-xs">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE_VALUE}>—</SelectItem>
                              {MUCUS_CONSISTENCY_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />

                      {couple?.optional_fields?.mucus_color !== false && (
                        <FieldRow
                          label="Mucus color"
                          days={days}
                          drafts={drafts}
                          renderCell={(dateKey, draft) => (
                            <Select
                              disabled={!canWriteObservations}
                              value={draft.mucusColor ?? NONE_VALUE}
                              onValueChange={(value) =>
                                updateDraft(dateKey, {
                                  mucusColor: value === NONE_VALUE ? null : value,
                                })
                              }
                            >
                              <SelectTrigger className="h-9 text-xs">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE_VALUE}>—</SelectItem>
                                {MUCUS_COLOR_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      )}

                      {couple?.optional_fields?.mucus_frequency === true && (
                        <FieldRow
                          label="Mucus frequency"
                          days={days}
                          drafts={drafts}
                          renderCell={(dateKey, draft) => (
                            <Select
                              disabled={!canWriteObservations}
                              value={draft.mucusFrequency ?? NONE_VALUE}
                              onValueChange={(value) =>
                                updateDraft(dateKey, {
                                  mucusFrequency: value === NONE_VALUE ? null : value,
                                })
                              }
                            >
                              <SelectTrigger className="h-9 text-xs">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE_VALUE}>—</SelectItem>
                                {MUCUS_FREQUENCY_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      )}

                      <FieldRow
                        label="Monitor"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <Select
                            disabled={!canWriteObservations}
                            value={draft.monitor ?? NONE_VALUE}
                            onValueChange={(value) =>
                              updateDraft(dateKey, {
                                monitor:
                                  value === NONE_VALUE ? null : (value as NonNullable<MonitorValue>),
                              })
                            }
                          >
                            <SelectTrigger className="h-9 text-xs">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE_VALUE}>—</SelectItem>
                              {MONITOR_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />

                      {couple?.optional_fields?.ovulation_test === true && (
                        <FieldRow
                          label="Ovulation test"
                          days={days}
                          drafts={drafts}
                          renderCell={(dateKey, draft) => (
                            <Select
                              disabled={!canWriteObservations}
                              value={draft.ovulationTest ?? NONE_VALUE}
                              onValueChange={(value) =>
                                updateDraft(dateKey, {
                                  ovulationTest:
                                    value === NONE_VALUE
                                      ? null
                                      : (value as OvulationTestResult),
                                })
                              }
                            >
                              <SelectTrigger className="h-9 text-xs">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE_VALUE}>—</SelectItem>
                                {OVULATION_TEST_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      )}

                      <FieldRow
                        label="Temp (°F)"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <Input
                            value={draft.temperature}
                            disabled={!canWriteObservations}
                            onChange={(e) => updateDraft(dateKey, { temperature: e.target.value })}
                            placeholder="—"
                            inputMode="decimal"
                            className="mx-auto h-9 w-20 text-center text-xs"
                          />
                        )}
                      />

                      <FieldRow
                        label="Intercourse"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <Switch
                            checked={draft.intercourse}
                            disabled={!canWriteObservations}
                            onCheckedChange={(checked) =>
                              updateDraft(dateKey, { intercourse: checked })
                            }
                          />
                        )}
                      />

                      <FieldRow
                        label="Symptoms"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <div className="flex flex-wrap justify-center gap-1">
                            {SYMPTOM_OPTIONS.map((opt) => {
                              const active = draft.symptoms.includes(opt.name);
                              const Icon = opt.icon;
                              return (
                                <Tooltip key={opt.name}>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      aria-label={opt.name}
                                      disabled={!canWriteObservations}
                                      onClick={() => toggleSymptom(dateKey, opt.name)}
                                      className={cn(
                                        "flex h-6 w-6 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed",
                                        active
                                          ? "border-primary bg-primary/15"
                                          : "border-border bg-muted/30 opacity-50 hover:opacity-100"
                                      )}
                                    >
                                      <Icon className={cn("h-3 w-3", active && opt.color)} />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">{opt.name}</TooltipContent>
                                </Tooltip>
                              );
                            })}
                          </div>
                        )}
                      />

                      <FieldRow
                        label="Notes"
                        days={days}
                        drafts={drafts}
                        renderCell={(dateKey, draft) => (
                          <Input
                            value={draft.notes}
                            disabled={!canWriteObservations}
                            onChange={(e) => updateDraft(dateKey, { notes: e.target.value })}
                            placeholder="—"
                            className="h-9 min-w-[110px] text-xs"
                          />
                        )}
                      />
                    </TableBody>
                  </table>
                </div>

                {/* Scroll-fade edges — the only cue that the grid scrolls both ways, since the
                 * sticky header/first-column already look "in place" and give no other hint. */}
                <div
                  className={cn(
                    "pointer-events-none absolute inset-y-0 left-0 z-40 w-6 bg-gradient-to-r from-background to-transparent transition-opacity",
                    scrollEdges.left ? "opacity-100" : "opacity-0"
                  )}
                />
                <div
                  className={cn(
                    "pointer-events-none absolute inset-y-0 right-0 z-40 w-6 bg-gradient-to-l from-background to-transparent transition-opacity",
                    scrollEdges.right ? "opacity-100" : "opacity-0"
                  )}
                />
                <div
                  className={cn(
                    "pointer-events-none absolute inset-x-0 top-0 z-40 h-6 bg-gradient-to-b from-background to-transparent transition-opacity",
                    scrollEdges.top ? "opacity-100" : "opacity-0"
                  )}
                />
                <div
                  className={cn(
                    "pointer-events-none absolute inset-x-0 bottom-0 z-40 h-6 bg-gradient-to-t from-background to-transparent transition-opacity",
                    scrollEdges.bottom ? "opacity-100" : "opacity-0"
                  )}
                />
              </>
            )}
          </Card>
        </div>
      </DialogContent>

      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              {dirtyDates.size} unsaved day{dirtyDates.size === 1 ? "" : "s"} will be lost if
              you close now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                trackEvent("bulk_entry_discarded", { dayCount: dirtyDates.size });
                closeAndLeave();
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default BulkEntry;
